// acta_machine_catalog_v4_optimized_list
// acta_machine_catalog_v3_photo
// acta_machine_catalog_v2
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Archive,
  CaretDown,
  DotsThreeVertical,
  ImageSquare,
  MagnifyingGlass,
  Package,
  PencilSimple,
  Plus,
  Trash,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  machineCatalog as machineCatalogApi,
} from "@/api";
import { formatApiError } from "@/lib/api";
import {
  SortableTableHeader,
} from "@/components/ui/SortableTableHeader";
import {
  Card,
} from "@/components/ui/card";
import {
  ActaActionMenu,
  ActaActionMenuItem,
  ActaActionSheet,
} from "@/components/ui/ActaActionMenu";
import {
  useTableSort,
} from "@/hooks/useTableSort";
import {
  sortRows,
} from "@/lib/tableSort";

const TYPES = [
  { value: "machine", label: "Machines" },
  { value: "handpiece", label: "Pièces à main" },
  { value: "accessory", label: "Accessoires" },
  { value: "consumable", label: "Consommables" },
  { value: "pack", label: "Packs" },
];

const EMPTY = {
  name: "",
  type: "machine",
  family: "",
  manufacturer: "",
  model_name: "",
  reference: "",
  notes: "",
  compatible_machine_ids: [],
  active: true,
};

const PAGE_SIZE = 12;

const CATALOG_TYPE_FILTERS = [
  {
    value: "",
    label: "Tous",
  },
  {
    value: "machine",
    label: "Machines",
  },
  {
    value: "handpiece",
    label: "Pièces à main",
  },
  {
    value: "accessory",
    label: "Accessoires",
  },
];

const typeLabel = (value) =>
  TYPES.find(
    (item) => item.value === value
  )?.label || "Machines";

const apiError = (error) =>
  formatApiError(
    error?.response?.data?.detail
  ) ||
  error?.message ||
  "Une erreur est survenue";

const SORT_ACCESSORS = {
  name: (item) =>
    item.name || "",
  reference: (item) =>
    item.reference || "",
  type: (item) =>
    typeLabel(item.type || "machine"),
  manufacturer: (item) =>
    item.manufacturer || "",
  model: (item) =>
    item.modelName || "",
  status: (item) =>
    item.active === false
      ? "Archivé"
      : "Actif",
};

export default function MachineCatalogSettings({
  embedded = false,
}) {
  const [items, setItems] =
    useState([]);
  const [loading, setLoading] =
    useState(true);
  const [form, setForm] =
    useState(EMPTY);
  const [editingId, setEditingId] =
    useState("");
  const [modalOpen, setModalOpen] =
    useState(false);
  const [detailItem, setDetailItem] =
    useState(null);
  const [saving, setSaving] =
    useState(false);
  const [photoFile, setPhotoFile] =
    useState(null);
  const [removePhoto, setRemovePhoto] =
    useState(false);
  const [search, setSearch] =
    useState("");
  const [typeFilter, setTypeFilter] =
    useState("");
  const [page, setPage] =
    useState(1);
  const [actionMenu, setActionMenu] =
    useState(null);
  const [
    mobileActionItem,
    setMobileActionItem,
  ] = useState(null);

  const {
    sortBy,
    sortOrder,
    toggleSort,
  } = useTableSort("name");

  const load = async () => {
    setLoading(true);

    try {
      const result =
        await machineCatalogApi.list({
          includeInactive: true,
        });

      setItems(
        Array.isArray(result)
          ? result
          : []
      );
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const machines = useMemo(
    () =>
      items.filter(
        (item) =>
          (item.type || "machine") ===
            "machine" &&
          item.active !== false
      ),
    [items]
  );

  const filteredItems = useMemo(() => {
    const query = search
      .trim()
      .toLocaleLowerCase("fr");

    return items.filter((item) => {
      const itemType =
        item.type || "machine";

      if (
        typeFilter &&
        itemType !== typeFilter
      ) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [
        item.name,
        item.reference,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value)
            .toLocaleLowerCase("fr")
            .includes(query)
        );
    });
  }, [
    items,
    search,
    typeFilter,
  ]);

  const sortedItems = useMemo(
    () =>
      sortRows(filteredItems, {
        sortBy,
        sortOrder,
        accessors: SORT_ACCESSORS,
      }),
    [
      filteredItems,
      sortBy,
      sortOrder,
    ]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(
      sortedItems.length / PAGE_SIZE
    )
  );

  const visibleItems = useMemo(
    () =>
      sortedItems.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
      ),
    [sortedItems, page]
  );

  useEffect(() => {
    setPage(1);
  }, [
    search,
    typeFilter,
  ]);

  useEffect(() => {
    setPage((current) =>
      Math.min(current, totalPages)
    );
  }, [totalPages]);

  const openCreate = () => {
    setEditingId("");
    setForm(EMPTY);
    setPhotoFile(null);
    setRemovePhoto(false);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      type: item.type || "machine",
      family: item.family || "",
      manufacturer:
        item.manufacturer || "",
      model_name:
        item.modelName || "",
      reference:
        item.reference || "",
      notes: item.notes || "",
      compatible_machine_ids:
        item.compatibleMachineIds || [],
      active:
        item.active !== false,
      image_url:
        item.imageUrl || "",
    });

    setPhotoFile(null);
    setRemovePhoto(false);
    setDetailItem(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingId("");
    setForm(EMPTY);
    setPhotoFile(null);
    setRemovePhoto(false);
  };

  const save = async () => {
    if (!form.name.trim()) {
      return toast.error(
        "Nom obligatoire"
      );
    }

    if (!form.reference.trim()) {
      return toast.error(
        "Référence obligatoire"
      );
    }

    setSaving(true);

    try {
      const item = editingId
        ? await machineCatalogApi.update(
            editingId,
            form
          )
        : await machineCatalogApi.create(
            form
          );

      if (
        removePhoto &&
        item.imageUrl
      ) {
        await machineCatalogApi.deletePhoto(
          item.id
        );
      }

      if (photoFile) {
        await machineCatalogApi.uploadPhoto(
          item.id,
          photoFile
        );
      }

      toast.success(
        editingId
          ? "Référence mise à jour"
          : "Référence ajoutée"
      );

      setModalOpen(false);
      setEditingId("");
      setForm(EMPTY);

      await load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (item) => {
    if (
      !window.confirm(
        `Archiver ${item.name} ?`
      )
    ) {
      return;
    }

    try {
      await machineCatalogApi.archive(
        item.id
      );

      toast.success(
        "Référence archivée"
      );

      setDetailItem(null);
      await load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const closeActions = () => {
    setActionMenu(null);
    setMobileActionItem(null);
  };

  const openActions = (
    event,
    item
  ) => {
    const isMobile =
      typeof window !==
        "undefined" &&
      window
        .matchMedia(
          "(max-width: 767px)"
        )
        .matches;

    if (isMobile) {
      setActionMenu(null);
      setMobileActionItem(item);
      return;
    }

    setMobileActionItem(null);
    setActionMenu({
      item,
      anchorEl:
        event.currentTarget,
    });
  };

  const runViewAction = (item) => {
    closeActions();
    setDetailItem(item);
  };

  const runEditAction = (item) => {
    closeActions();
    openEdit(item);
  };

  const runArchiveAction = (
    item
  ) => {
    closeActions();
    archive(item);
  };

  const statusBadge = (item) => (
    <span
      className={
        item.active === false
          ? "inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-bg-2)] px-2 py-0.5 text-[11px] font-heading font-semibold text-[var(--do-muted)]"
          : "inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2 py-0.5 text-[11px] font-heading font-semibold text-[var(--do-accent)]"
      }
    >
      {item.active === false
        ? "Archivé"
        : "Actif"}
    </span>
  );

  return (
    <div
      className={
        embedded
          ? "w-full min-w-0"
          : "mx-auto max-w-[1500px] p-4 sm:p-6 lg:p-8"
      }
      data-testid="machine-catalog-settings-page"
    >
      <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="relative block min-w-0 flex-1">
          <MagnifyingGlass
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
          />

          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Rechercher un nom ou une référence"
            className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-4 text-sm outline-none transition focus:border-[var(--do-accent)]"
            data-testid="machine-catalog-search"
          />
        </label>

        <button
          type="button"
          onClick={openCreate}
          className="au-btn-primary inline-flex h-11 shrink-0 items-center justify-center gap-2 px-5 text-sm"
          data-testid="machine-catalog-add"
        >
          <Plus
            size={18}
            weight="bold"
          />
          Ajouter une référence
        </button>
      </header>

      <div
        className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="machine-catalog-type-filters"
      >
        {CATALOG_TYPE_FILTERS.map(
          (filter) => {
            const active =
              typeFilter === filter.value;

            const count =
              filter.value
                ? items.filter(
                    (item) =>
                      (item.type ||
                        "machine") ===
                      filter.value
                  ).length
                : items.length;

            return (
              <Card
                key={filter.value || "all"}
                variant={
                  active
                    ? "actaSubtle"
                    : "actaInteractive"
                }
                className={
                  active
                    ? "border-[var(--do-accent)]"
                    : ""
                }
              >
                <button
                  type="button"
                  onClick={() =>
                    setTypeFilter(
                      filter.value
                    )
                  }
                  className="flex w-full items-center justify-between gap-3 p-4 text-left"
                  aria-pressed={active}
                  data-testid={`catalog-filter-${
                    filter.value || "all"
                  }`}
                >
                  <span className="font-heading text-sm font-bold text-[var(--do-ink)]">
                    {filter.label}
                  </span>

                  <span
                    className={
                      active
                        ? "text-sm font-black text-[var(--do-accent)]"
                        : "text-sm font-black text-[var(--do-muted)]"
                    }
                  >
                    {count}
                  </span>
                </button>
              </Card>
            );
          }
        )}
      </div>

      {loading ? (
        <div
          className="label-eyebrow lirah-pulse"
          data-testid="machine-catalog-loading"
        >
          Chargement
        </div>
      ) : (
        <>
          <div
            className="lirah-card users-desktop-table overflow-hidden"
            data-testid="machine-catalog-table"
          >
            <div className="table-responsive table-scroll">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--do-border)] bg-[var(--do-bg-solid)]">
                  <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[var(--do-muted)]">
                    <SortableTableHeader
                      label="Nom"
                      field="name"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-2.5 font-semibold"
                      testId="catalog-sort-name"
                    />

                    <SortableTableHeader
                      label="Référence"
                      field="reference"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-2.5 font-semibold"
                      testId="catalog-sort-reference"
                    />

                    <SortableTableHeader
                      label="Catégorie"
                      field="type"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-2.5 font-semibold"
                      testId="catalog-sort-type"
                    />

                    <SortableTableHeader
                      label="Fabricant"
                      field="manufacturer"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-2.5 font-semibold"
                      testId="catalog-sort-manufacturer"
                    />

                    <SortableTableHeader
                      label="Modèle"
                      field="model"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-2.5 font-semibold"
                      testId="catalog-sort-model"
                    />

                    <SortableTableHeader
                      label="Statut"
                      field="status"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-2.5 font-semibold"
                      testId="catalog-sort-status"
                    />

                    <th className="w-20 px-4 py-2.5 text-right font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {visibleItems.map(
                    (item) => (
                      <tr
                        key={item.id}
                        className="border-b border-[var(--do-border)] hover:bg-[var(--do-bg-solid)]"
                        data-testid={`machine-catalog-row-${item.id}`}
                      >
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={() =>
                              setDetailItem(
                                item
                              )
                            }
                            className="max-w-[320px] truncate text-left font-heading font-semibold text-[var(--do-ink)] hover:text-[var(--do-accent)] hover:underline"
                            data-testid={`catalog-open-name-${item.id}`}
                          >
                            {item.name}
                          </button>
                        </td>

                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={() =>
                              setDetailItem(
                                item
                              )
                            }
                            className="font-mono-stat text-xs font-bold text-[var(--do-ink)] hover:text-[var(--do-accent)] hover:underline"
                            data-testid={`catalog-open-reference-${item.id}`}
                          >
                            {item.reference ||
                              "—"}
                          </button>
                        </td>

                        <td className="px-4 py-2.5 text-xs text-[var(--do-muted)]">
                          {typeLabel(
                            item.type ||
                              "machine"
                          )}
                        </td>

                        <td className="px-4 py-2.5 text-xs text-[var(--do-muted)]">
                          {item.manufacturer ||
                            "—"}
                        </td>

                        <td className="px-4 py-2.5 text-xs text-[var(--do-muted)]">
                          {item.modelName ||
                            "—"}
                        </td>

                        <td className="px-4 py-2.5">
                          {statusBadge(
                            item
                          )}
                        </td>

                        <td className="px-4 py-2.5 text-right">
                          <div className="documents-v2-action-menu">
                            <button
                              type="button"
                              className="acta-action-trigger documents-v2-action-trigger"
                              aria-expanded={
                                actionMenu
                                  ?.item
                                  ?.id ===
                                item.id
                              }
                              onClick={(
                                event
                              ) =>
                                openActions(
                                  event,
                                  item
                                )
                              }
                              data-testid={`actions-catalog-${item.id}`}
                            >
                              <DotsThreeVertical
                                size={16}
                                weight="bold"
                              />
                              Actions
                              <CaretDown
                                size={13}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  )}

                  {visibleItems.length ===
                    0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-8 text-center"
                      >
                        <Package
                          size={30}
                          className="mx-auto text-[var(--do-muted)]"
                        />

                        <div className="mt-2 text-sm font-semibold text-[var(--do-ink)]">
                          Aucun résultat
                        </div>

                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          Recherchez un nom
                          ou une référence.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            className="users-mobile-cards overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]"
            data-testid="machine-catalog-mobile-list"
          >
            {visibleItems.length ===
            0 ? (
              <div className="p-6 text-center text-sm text-[var(--do-muted)]">
                Aucun résultat.
              </div>
            ) : (
              visibleItems.map(
                (item) => (
                  <div
                    key={`mobile-${item.id}`}
                    className="border-b border-[var(--do-border)] p-4 last:border-b-0"
                    data-testid={`machine-catalog-mobile-row-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() =>
                            setDetailItem(
                              item
                            )
                          }
                          className="block max-w-full truncate text-left font-heading text-sm font-black text-[var(--do-ink)] hover:text-[var(--do-accent)]"
                        >
                          {item.name}
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setDetailItem(
                              item
                            )
                          }
                          className="mt-1 block max-w-full truncate text-left font-mono-stat text-xs font-bold text-[var(--do-muted)] hover:text-[var(--do-accent)]"
                        >
                          {item.reference ||
                            "—"}
                        </button>
                      </div>

                      {statusBadge(item)}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
                      <div>
                        <span className="text-[var(--do-muted)]">
                          Catégorie
                        </span>
                        <div className="mt-1 font-bold text-[var(--do-ink)]">
                          {typeLabel(
                            item.type ||
                              "machine"
                          )}
                        </div>
                      </div>

                      <div>
                        <span className="text-[var(--do-muted)]">
                          Fabricant
                        </span>
                        <div className="mt-1 truncate font-bold text-[var(--do-ink)]">
                          {item.manufacturer ||
                            "—"}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-xs font-heading font-bold"
                      onClick={(
                        event
                      ) =>
                        openActions(
                          event,
                          item
                        )
                      }
                      data-testid={`mobile-actions-catalog-${item.id}`}
                    >
                      <DotsThreeVertical
                        size={17}
                        weight="bold"
                      />
                      Actions
                    </button>
                  </div>
                )
              )
            )}
          </div>

          {sortedItems.length >
            PAGE_SIZE && (
            <nav className="mt-5 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={page === 1}
                onClick={() =>
                  setPage(
                    (current) =>
                      Math.max(
                        1,
                        current - 1
                      )
                  )
                }
                className="rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold disabled:opacity-40"
              >
                Précédent
              </button>

              <span className="text-sm text-[var(--do-muted)]">
                Page {page} sur{" "}
                {totalPages}
              </span>

              <button
                type="button"
                disabled={
                  page === totalPages
                }
                onClick={() =>
                  setPage(
                    (current) =>
                      Math.min(
                        totalPages,
                        current + 1
                      )
                  )
                }
                className="rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold disabled:opacity-40"
              >
                Suivant
              </button>
            </nav>
          )}
        </>
      )}

      <ActaActionMenu
        open={Boolean(actionMenu)}
        anchorEl={
          actionMenu?.anchorEl
        }
        onClose={() =>
          setActionMenu(null)
        }
        width={230}
        estimatedHeight={180}
        testId="catalog-action-menu"
      >
        {actionMenu?.item && (
          <>
            <ActaActionMenuItem
              icon={
                <Package size={18} />
              }
              label="Voir"
              onClick={() =>
                runViewAction(
                  actionMenu.item
                )
              }
              testId={`view-catalog-${actionMenu.item.id}`}
            />

            <ActaActionMenuItem
              icon={
                <PencilSimple
                  size={18}
                />
              }
              label="Modifier"
              onClick={() =>
                runEditAction(
                  actionMenu.item
                )
              }
              testId={`edit-catalog-${actionMenu.item.id}`}
            />

            {actionMenu.item
              .active !==
              false && (
              <ActaActionMenuItem
                icon={
                  <Archive
                    size={18}
                  />
                }
                label="Archiver"
                danger
                onClick={() =>
                  runArchiveAction(
                    actionMenu.item
                  )
                }
                testId={`archive-catalog-${actionMenu.item.id}`}
              />
            )}
          </>
        )}
      </ActaActionMenu>

      <ActaActionSheet
        open={Boolean(
          mobileActionItem
        )}
        title="Actions"
        subtitle={
          mobileActionItem
            ? `${mobileActionItem.name || ""} · ${mobileActionItem.reference || ""}`
            : ""
        }
        onClose={() =>
          setMobileActionItem(null)
        }
        testId="catalog-mobile-action-sheet"
      >
        {mobileActionItem && (
          <>
            <ActaActionMenuItem
              icon={
                <Package size={18} />
              }
              label="Voir"
              onClick={() =>
                runViewAction(
                  mobileActionItem
                )
              }
              testId={`mobile-view-catalog-${mobileActionItem.id}`}
            />

            <ActaActionMenuItem
              icon={
                <PencilSimple
                  size={18}
                />
              }
              label="Modifier"
              onClick={() =>
                runEditAction(
                  mobileActionItem
                )
              }
              testId={`mobile-edit-catalog-${mobileActionItem.id}`}
            />

            {mobileActionItem
              .active !==
              false && (
              <ActaActionMenuItem
                icon={
                  <Archive
                    size={18}
                  />
                }
                label="Archiver"
                danger
                onClick={() =>
                  runArchiveAction(
                    mobileActionItem
                  )
                }
                testId={`mobile-archive-catalog-${mobileActionItem.id}`}
              />
            )}
          </>
        )}
      </ActaActionSheet>

      {detailItem && (
        <Drawer
          item={detailItem}
          allItems={items}
          onClose={() =>
            setDetailItem(null)
          }
          onEdit={() =>
            openEdit(detailItem)
          }
          onArchive={() =>
            archive(detailItem)
          }
        />
      )}

      {modalOpen && (
        <Modal
          form={form}
          setForm={setForm}
          machines={machines}
          editing={Boolean(
            editingId
          )}
          saving={saving}
          photoFile={photoFile}
          setPhotoFile={
            setPhotoFile
          }
          removePhoto={
            removePhoto
          }
          setRemovePhoto={
            setRemovePhoto
          }
          onClose={closeModal}
          onSave={save}
        />
      )}
    </div>
  );
}

function ProductImage({
  item,
  large = false,
}) {
  if (item.imageUrl) {
    return (
      <img
        src={item.imageUrl}
        alt=""
        className={`w-full object-contain ${
          large
            ? "h-72"
            : "h-44"
        }`}
      />
    );
  }

  return (
    <div
      className={`flex w-full items-center justify-center bg-[var(--do-bg-solid)] text-[var(--do-muted)] ${
        large
          ? "h-72"
          : "h-44"
      }`}
    >
      <ImageSquare
        size={large ? 72 : 48}
        weight="duotone"
      />
    </div>
  );
}

function Drawer({item, allItems, onClose, onEdit, onArchive}) {
  const compatible = (item.compatibleMachineIds || []).map((id)=>allItems.find((x)=>x.id===id)).filter(Boolean);
  return <div className="fixed inset-0 z-[80] flex justify-end bg-[var(--do-overlay)]" role="dialog" data-acta-modal-kind="drawer"><button className="absolute inset-0" onClick={onClose} aria-label="Fermer"/><aside className="relative z-10 h-full w-full overflow-y-auto bg-[var(--do-bg-solid)] shadow-2xl sm:max-w-xl"><ProductImage item={item} large/><div className="p-5 sm:p-7"><div className="flex items-start justify-between"><div><span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-accent)]">{typeLabel(item.type || "machine")}</span><h2 className="mt-3 font-heading text-2xl font-black">{item.name}</h2></div><button onClick={onClose} className="rounded-xl p-2"><X size={22}/></button></div><dl className="mt-7 grid gap-4 rounded-2xl border border-[var(--do-border)] p-5 text-sm"><Detail label="Référence" value={item.reference}/><Detail label="Fabricant" value={item.manufacturer}/><Detail label="Gamme" value={item.family}/><Detail label="Modèle" value={item.modelName}/><Detail label="Notes" value={item.notes}/></dl>{compatible.length>0 && <div className="mt-6"><h3 className="font-heading text-lg font-black">Machines compatibles</h3><div className="mt-3 grid gap-2">{compatible.map((m)=><div key={m.id} className="rounded-xl border border-[var(--do-border)] p-3 text-sm"><div className="font-bold">{m.name}</div><div className="mt-1 font-mono text-xs text-[var(--do-muted)]">{m.reference}</div></div>)}</div></div>}<div className="mt-7 flex gap-3"><button onClick={onEdit} className="au-btn-primary px-5 py-3 text-sm">Modifier</button>{item.active !== false && <button onClick={onArchive} className="rounded-xl border border-[var(--do-border)] px-5 py-3 text-sm font-bold text-[var(--do-accent)]">Archiver</button>}</div></div></aside></div>;
}

function Modal({form,setForm,machines,editing,saving,photoFile,setPhotoFile,removePhoto,setRemovePhoto,onClose,onSave}) {
  const fileInput = useRef(null);
  const showCompatibility = !["machine","pack"].includes(form.type);
  return <div className="acta-modal-overlay fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6" role="dialog"><button className="absolute inset-0" onClick={onClose} aria-label="Fermer"/><section className="acta-modal-surface acta-modal-scroll relative z-10 max-h-[95dvh] w-full max-w-2xl rounded-3xl p-5 sm:p-7" data-testid="machine-catalog-form"><div className="flex items-center justify-between"><div><div className="label-eyebrow">Catalogue</div><h2 className="mt-1 font-heading text-2xl font-black">{editing ? "Modifier la référence" : "Ajouter une référence"}</h2></div><button onClick={onClose}><X size={22}/></button></div><div className="mt-6 grid gap-6 lg:grid-cols-[200px_1fr]"><div><div className="overflow-hidden rounded-2xl border border-[var(--do-border)]">{photoFile || (!removePhoto && form.image_url) ? <img src={photoFile ? URL.createObjectURL(photoFile) : form.image_url} alt="" className="h-48 w-full object-contain"/> : <div className="flex h-48 items-center justify-center bg-[var(--do-bg-solid)] text-[var(--do-muted)]"><ImageSquare size={52} weight="duotone"/></div>}</div><input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e)=>{const file=e.target.files?.[0]||null;setPhotoFile(file);if(file)setRemovePhoto(false);}}/><button type="button" onClick={()=>fileInput.current?.click()} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] px-3 py-2 text-sm font-bold"><UploadSimple size={17}/>Ajouter / remplacer</button>{(photoFile || (!removePhoto && form.image_url)) && <button type="button" onClick={()=>{setPhotoFile(null);setRemovePhoto(true);}} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--do-border)] px-3 py-2 text-sm font-bold text-[var(--do-accent)]"><Trash size={17}/>Retirer</button>}<p className="mt-2 text-xs text-[var(--do-muted)]">JPG, PNG ou WebP, 5 Mo maximum.</p></div><div><div className="grid gap-4 sm:grid-cols-2"><Field label="Type"><select value={form.type} onChange={(e)=>setForm((c)=>({...c,type:e.target.value,compatible_machine_ids:e.target.value==="machine"?[]:c.compatible_machine_ids}))} className="catalog-input">{TYPES.map((t)=><option key={t.value} value={t.value}>{t.label}</option>)}</select></Field><Input label="Nom" value={form.name} onChange={(v)=>setForm((c)=>({...c,name:v}))} testId="machine-catalog-name"/><Input label="Référence" value={form.reference} onChange={(v)=>setForm((c)=>({...c,reference:v.toUpperCase()}))}/><Input label="Fabricant" value={form.manufacturer} onChange={(v)=>setForm((c)=>({...c,manufacturer:v}))}/><Input label="Famille / gamme" value={form.family} onChange={(v)=>setForm((c)=>({...c,family:v}))}/><Input label="Modèle" value={form.model_name} onChange={(v)=>setForm((c)=>({...c,model_name:v}))}/></div>{showCompatibility && <Field label="Machines compatibles" className="mt-4"><div className="max-h-44 overflow-y-auto rounded-xl border border-[var(--do-border)] p-3">{machines.map((m)=>{const checked=form.compatible_machine_ids.includes(m.id);return <label key={m.id} className="flex items-center gap-3 py-2 text-sm"><input type="checkbox" checked={checked} onChange={()=>setForm((c)=>({...c,compatible_machine_ids:checked?c.compatible_machine_ids.filter((id)=>id!==m.id):[...c.compatible_machine_ids,m.id]}))}/><span className="font-bold">{m.name}</span><span className="font-mono text-xs text-[var(--do-muted)]">{m.reference}</span></label>;})}</div></Field>}<Field label="Notes" className="mt-4"><textarea value={form.notes} onChange={(e)=>setForm((c)=>({...c,notes:e.target.value}))} rows={4} className="catalog-input"/></Field></div></div><div className="sticky bottom-0 -mx-5 mt-6 flex justify-end gap-3 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 pt-4 sm:-mx-7 sm:px-7"><button onClick={onClose} className="rounded-xl border border-[var(--do-border)] px-5 py-3 text-sm font-bold">Annuler</button><button onClick={onSave} disabled={saving} className="au-btn-primary px-5 py-3 text-sm disabled:opacity-60" data-testid="machine-catalog-save">{saving?"Enregistrement…":"Enregistrer"}</button></div></section></div>;
}

function Input({label,value,onChange,testId}) { return <Field label={label}><input value={value} onChange={(e)=>onChange(e.target.value)} className="catalog-input" data-testid={testId}/></Field>; }
function Field({label,children,className=""}) { return <label className={`block ${className}`}><span className="mb-2 block text-xs font-bold text-[var(--do-muted)]">{label}</span>{children}</label>; }
function Detail({label,value}) { return <div><dt className="text-xs font-bold uppercase tracking-wide text-[var(--do-muted)]">{label}</dt><dd className="mt-1 whitespace-pre-wrap font-medium">{value || "—"}</dd></div>; }
